import {
  type DiscoverSessionProps,
  getDiscoverSessionProps,
} from "@/lib/analytics/discover-attribution";
import { trackFirstDiscoverInteraction } from "@/lib/analytics/discoverInteractions";
import {
  type DiscoverTab,
  type ListingFilters,
  NO_FILTERS,
  toListingFilters,
} from "@/lib/analytics/listingFilters";
import { type EntryPoint, getEntryPoint } from "@/lib/analytics/tracking-utils";

/**
 * Context for events fired on a detail page. The detail URL carries no
 * filters, so the listing the visitor came from saves its filters to
 * `sessionStorage` (a session is one browser tab) and the detail page reads them
 * back.
 */

const LISTING_KEY_PREFIX = "discover-listing:";
const SEARCH_SUBMITTED_KEY = "discover-search-submitted";

/**
 * Save the listing's filters. Call it when the listing renders and whenever
 * its URL changes.
 */
export function rememberDiscoverListing(
  searchParams: URLSearchParams | null | undefined,
  tab: DiscoverTab,
): void {
  if (typeof window === "undefined") return;

  const params = searchParams ?? new URLSearchParams();
  try {
    window.sessionStorage.setItem(
      LISTING_KEY_PREFIX + tab,
      JSON.stringify(toListingFilters(params, tab)),
    );
  } catch {
    // Ignore storage failures; the event then carries no filters.
  }
}

/**
 * The visitor made a search in this tab: a Search press or a query in the
 * desktop search box. A `?q` already in the URL, such as a shared link, is not
 * a search. `search_submitted` stays set for the tab.
 */
export function trackSearchSubmitted(): void {
  if (typeof window === "undefined") return;
  trackFirstDiscoverInteraction("search");
  try {
    window.sessionStorage.setItem(SEARCH_SUBMITTED_KEY, "1");
  } catch {
    // Ignore storage failures; search_submitted then stays false.
  }
}

function readRememberedFilters(tab: DiscoverTab): ListingFilters {
  try {
    const raw = window.sessionStorage.getItem(LISTING_KEY_PREFIX + tab);
    return raw ? { ...NO_FILTERS, ...JSON.parse(raw) } : NO_FILTERS;
  } catch {
    return NO_FILTERS;
  }
}

function readSearchSubmitted(): boolean {
  try {
    return window.sessionStorage.getItem(SEARCH_SUBMITTED_KEY) !== null;
  } catch {
    return false;
  }
}

export type DiscoverContextProps = DiscoverSessionProps &
  ListingFilters & {
    entry_point: EntryPoint;
    search_submitted: boolean;
  };

/**
 * What every event on a detail page carries besides its identity: where the
 * visitor came from (entry point and `referrer`), the origin and surface, the
 * listing's filters and whether they searched. Call it in the browser, from the
 * event's callback.
 */
export function getDiscoverContextProps({
  searchParams,
  embeddedInApp,
  tab,
}: {
  searchParams: URLSearchParams | null | undefined;
  embeddedInApp: boolean;
  tab: DiscoverTab;
}): DiscoverContextProps {
  const entryPoint = getEntryPoint(searchParams?.get("ref") ?? null);
  // Only a view that came from a list has the list's filters. A shared link
  // opened in a tab that browsed with filters must not inherit them.
  const cameFromList =
    entryPoint === "discover_list" || entryPoint === "discover_map";

  return {
    entry_point: entryPoint,
    ...getDiscoverSessionProps(searchParams, embeddedInApp),
    ...(cameFromList ? readRememberedFilters(tab) : NO_FILTERS),
    search_submitted: readSearchSubmitted(),
  };
}
